import { UserCircleIcon, ExternalIcon, HelpIcon } from '@setel/portal-ui';
import tw from 'twin.macro';
import {
  useGetTicketTags,
  useGetUserById,
  useGetUserVerificationsById,
  useGetUserWalletById,
} from './accounts.queries';
import * as React from 'react';
import { LoadingPage } from 'modules/pages/loading-page';
import { environments } from 'environments/environment';
import { AxiosError } from 'axios';
import {
  NavigationDropdown,
  NavigationDropdownSearchQueryKey,
  NavigationDropdownSearchQueryValue,
} from 'components/navigation-dropdown';
import { useSearchParams } from 'hooks/router.hooks';
import { FuelOrdersTable } from 'modules/fuel-orders/components/fuel-orders-listing';
import { StoreOrdersTable } from 'modules/store-order/components/store-order-listing';
import { LoyaltyInfo } from 'modules/loyalty/components/loyalty-details';
import { UserProfile } from 'modules/accounts/user-profile';
import { IUserProfile } from 'services/api-accounts.service';
import { TopupAndPaymentInfo } from 'modules/topup-and-payments/components/topup-and-payment-listing';
import { useLocation } from 'react-router-dom';
import { pageview } from 'shared/analytics/gtag';
import { IUserWalletParams } from 'services/api-ops.service';
import { IUserVerifications } from 'services/api-verifications.service';
import { useAuth } from 'modules/auth/auth.context';
import { BadgeListing } from '../badges/components/badges-listing';
import { MembershipDetail } from 'modules/membership/component/membership';
import { UserAttributes } from 'modules/user-attributes/components/user-attributes';

export function AccountDetails({ userId }: { userId: string }) {
  // added ga for url tracking
  let location = useLocation();
  const { zendeskClient } = useAuth();

  React.useEffect(() => {
    pageview(location.pathname, 'Account Details');
  }, [location]);

  const { data: ticketTags } = useGetTicketTags(zendeskClient);

  const { data, isError, isLoading } = useGetUserById(userId, {
    retry: (retryCount, err) => {
      const error = err as AxiosError;
      return retryCount < 3 && error?.response?.status !== 404;
    },
  });
  const { data: walletData } = useGetUserWalletById(userId, {
    retry: (retryCount, err) => {
      const error = err as AxiosError;
      return retryCount < 3 && error?.response?.status !== 404;
    },
  });

  const { data: verificationData } = useGetUserVerificationsById(userId, {
    retry: (retryCount, err) => {
      const error = err as AxiosError;
      return retryCount < 3 && error?.response?.status !== 404;
    },
  });
  return (
    <>
      {isLoading && !data ? (
        <LoadingPage />
      ) : isError ? (
        <UserNotFoundPage />
      ) : (
        <React.Fragment>
          <NavigationDropdown />
          <div
            style={{ backgroundColor: '#f5f5f5' }}
            className="flex justify-between px-4 items-center py-4 border-gray-200 border border-b-0 border-t-0"
          >
            <div css={tw`inline-flex h-10 items-center`}>
              <UserCircleIcon css={tw`text-3xl text-gray-500 mr-3`} />
              <div css={tw`flex flex-col`}>
                <div className="text-base">{data?.fullName}</div>
                <div className="text-base text-lightgrey">{data?.phone}</div>
              </div>
            </div>
          </div>

          <DynamicContainer
            isUserProfileLoaded={!!data}
            userId={userId}
            userProfile={data}
            userWallet={walletData}
            userVerifications={verificationData}
            ticketTags={ticketTags}
          />
        </React.Fragment>
      )}
    </>
  );
}

function DynamicContainer({
  isUserProfileLoaded,
  userId,
  userProfile,
  userWallet,
  userVerifications,
  ticketTags,
}: ViewOptionProps) {
  const { searchParams } = useSearchParams();
  switch (searchParams.get(NavigationDropdownSearchQueryKey)) {
    case undefined:
    case null:
    case NavigationDropdownSearchQueryValue.USER_ATTRIBUTES:
      return <UserAttributes userId={userId} />;
    case NavigationDropdownSearchQueryValue.LOYALTY:
      return (
        <LoyaltyInfo
          userId={userId}
          isUserProfileLoaded={isUserProfileLoaded}
        />
      );
    case NavigationDropdownSearchQueryValue.FUEL_ORDER:
      return <FuelOrdersTable userId={userId} />;
    case NavigationDropdownSearchQueryValue.STORE_ORDER:
      return <StoreOrdersTable userId={userId} />;
    case NavigationDropdownSearchQueryValue.TOPUP_AND_PAYMENTS:
      return (
        <TopupAndPaymentInfo
          userId={userId}
          isUserProfileLoaded={isUserProfileLoaded}
        />
      );
    case NavigationDropdownSearchQueryValue.USER_PROFILE:
      return (
        <UserProfile
          userProfile={userProfile}
          userWallet={userWallet}
          userVerifications={userVerifications}
          ticketTags={ticketTags}
        />
      );
    case NavigationDropdownSearchQueryValue.ENGAGEMENT:
      return (
        <div css={tw`border-gray-200 border rounded-b-xl mb-5`}>
          <MembershipDetail userId={userId} />
          <BadgeListing userId={userId} />
          <hr className="mx-4" />
          <a
            rel="noreferrer"
            href={`${environments.webAdminUrl}/accounts/${userId}?tabIndex=2`}
            className="text-brand-500 tracking-1 font-semibold my-4 text-center flex justify-center"
            target="_blank"
          >
            <span className="text-xs">VIEW MORE </span>
            <ExternalIcon className="ml-2 inline" />
          </a>
        </div>
      );
    default:
      return <UserAttributes userId={userId} />;
  }
}

export function UserNotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center h-screen">
      <HelpIcon className="text-gray-500 h-9 w-9 mb-3.5" />
      <div className="text-center leading-5 mb-5">
        <p className="text-gray-500">Unable to find user.</p>
        <p className="text-gray-500">Information is incomplete.</p>
      </div>

      {/* <Button variant="primary" className="mb-5" onClick={() => {}}>
        SEARCH USER
      </Button> */}
      <a
        target="_blank"
        rel="noreferrer"
        href={`${environments.webAdminUrl}`}
        className="text-brand-500 tracking-1 font-semibold"
      >
        <span className="text-xs">OPEN PORTAL</span>
        <ExternalIcon className="inline ml-2" />
      </a>
    </div>
  );
}

export function CustomDescList({ children }: { children: React.ReactNode }) {
  return (
    <div
      css={tw`px-4 py-5 w-full`}
      // since this app is dealing with 320px screen size. It makes more sense to have a consistence interface
      style={{ gridTemplateColumns: '130px 155px', display: 'inline-grid' }}
    >
      {children}
    </div>
  );
}

export interface ViewOptionProps {
  userId: string;
  isUserProfileLoaded: boolean;
  userProfile?: IUserProfile | undefined;
  userWallet?: IUserWalletParams | undefined;
  userVerifications?: IUserVerifications | undefined;
  ticketTags?: string[] | [];
}
